import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { api } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Send, CheckCircle, Loader2, Lock } from 'lucide-react';

export default function Contact() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.auth.me().then(setUser).catch(() => setUser(null)).finally(() => setLoadingAuth(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      setError('Please enter a subject and message.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await api.functions.invoke('sendContactEmail', { subject, message });
      if (res.data?.success) {
        setSent(true);
      } else {
        setError(res.data?.error || 'Failed to send. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
        <Lock size={40} className="text-muted-foreground" strokeWidth={1.5} />
        <h2 className="font-heading text-xl font-semibold">Login Required</h2>
        <p className="text-sm text-muted-foreground">Please log in to send us a message.</p>
        <Button asChild className="rounded-xl mt-2">
          <Link to="/login">Log In</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="px-4 py-8 max-w-lg mx-auto">
        {sent ? (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <CheckCircle size={48} className="text-primary" strokeWidth={1.5} />
            <h2 className="font-heading text-xl font-semibold">Message Sent</h2>
            <p className="text-sm text-muted-foreground">Thank you for reaching out.<br />We'll get back to you as soon as possible.</p>
            <Button onClick={() => navigate('/')} variant="outline" className="mt-4 rounded-xl">
              Back to Home
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <p className="text-sm text-muted-foreground">
              Have a question or feedback? We'd love to hear from you.
            </p>

            <div className="space-y-1.5">
              <Label>Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Issue with a coffee listing"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Message</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write your message here..."
                className="rounded-xl resize-none"
                rows={6}
              />
            </div>

            {error && <p className="text-xs text-destructive">{error}</p>}

            <Button type="submit" disabled={submitting} className="w-full rounded-xl">
              {submitting ? (
                <Loader2 size={16} className="animate-spin mr-2" />
              ) : (
                <Send size={16} className="mr-2" />
              )}
              {submitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}